import { InjectionToken } from '@angular/core';
import { environment } from '@env/environment';
import { LogReplaceRules } from '../../interfaces';

export const DATADOG_LOGS_TOKEN = new InjectionToken<Partial<DataDogCustomConfig>>('datadog injection token');

/**
 * Datadogログ構成（カスタム用）
 */
export interface DataDogCustomConfig {
  /**
   * アプリケーションのサービス名
   * - フォーマット：all:spa:{アプリケーション名}
   * @example <caption>アプリケーションに応じて下記のように指定</caption>
   * all:spa:reservation
   * all:spa:servicing
   * all:spa:exchange
   * all:spa:checkin
   */
  service: string;
  /**
   * ログマスク対象の置換ルール
   * - デフォルトルール{@link logDefReplaceRules}以外に追加でルールを指定する場合
   * @type {?LogReplaceRules} {@link LogReplaceRules}
   */
  replaceRules?: LogReplaceRules;
}

/**
 * ログマスク対象置換ルール（デフォルト）
 */
export const logDefReplaceRules: LogReplaceRules = {
  // 銀行口座情報（口座名義）＝氏名：全桁をマスクする
  accountName: ['.', '*'],
  // メールアドレス：全桁をマスクする
  address: ['.', '*'],
  // 会員番号：全桁をマスクする
  amcMemberNumbers: ['.', '*'],
  // クレジットカード番号：末尾4桁以外をマスクする
  cardNumber: ['.(?=(.{4}))', '*'],
  // メールアドレス：全桁をマスクする
  confirmationEmail: ['.', '*'],
  // 連絡先国（ISO 3166-1）：全桁をマスクする
  countryCode: ['.', '*'],
  // 電話番号（国）：全桁をマスクする
  countryPhoneExtension: ['.', '*'],
  // ログインパスワード：全桁をマスクする
  customerPassword: ['\\d', '*'],
  // セキュリティコード：全桁をマスクする
  cvv: ['\\d', '*'],
  // メールアドレス：全桁をマスクする
  emailAddress: ['.', '*'],
  // 氏名：全桁をマスクする
  familyName: ['.', '*'],
  // 氏名：全桁をマスクする
  firstName: ['.', '*'],
  // 氏名：全桁をマスクする
  givenName: ['.', '*'],
  // クレジットカード名義＝氏名：全桁をマスクする
  holderName: ['.', '*'],
  // 氏名：全桁をマスクする
  lastName: ['.', '*'],
  // 会員番号：全桁をマスクする
  membershipNumber: ['.', '*'],
  // 氏名：全桁をマスクする
  middleName: ['.', '*'],
  // 氏名：全桁をマスクする
  name: ['.', '*'],
  // 電話番号：全桁をマスクする
  number: ['.', '*'],
  // 電話番号（国）：全桁をマスクする
  numberCountry: ['.', '*'],
  // 電話の持ち主：全桁をマスクする
  ownerNameOfPhone: ['.', '*'],
  // Cardinalから返却されるセッションID：全桁をマスクする
  partner3DSSessionId: ['.', '*'],
  // 会員番号：全桁をマスクする
  passengerMembershipNumber: ['.', '*'],
  // 電話番号（国）：全桁をマスクする
  phoneISDCode: ['.', '*'],
  // 電話番号：全桁をマスクする
  phoneNumber: ['.', '*'],
  // PIN：全桁をマスクする
  pin: ['.', '*'],
  // メールアドレス：全桁をマスクする
  preferredEmail: ['.', '*'],
  // 領収書宛名＝氏名：全桁をマスクする
  receiptAddress: ['.', '*'],
  // 宛名＝氏名：全桁をマスクする
  receivedFrom: ['.', '*'],
  // 発着メール受信者＝氏名：全桁をマスクする
  recipient: ['.', '*'],
  // 郵便番号：全桁をマスクする
  zipCode: ['.', '*'],
};

/**
 * Datadogログ構成（デフォルト）
 * - DatadogブラウザーSDK構成の詳細は公式を参照
 * {@link https://docs.datadoghq.com/ja/logs/log_collection/javascript/}
 */
export const datadogDefConfig = {
  clientToken: environment.datadog.clientToken,
  site: 'datadoghq.com',
  service: 'all:spa:default',
  env: environment.datadog.env,
};

/**
 * Datadogログ構成（デフォルト）
 */
type DatadogDefConfig = typeof datadogDefConfig;

/**
 * Datadogログ構成
 * @extends {DataDogCustomConfig}
 * @extends {DatadogDefConfig}
 */
export interface DataDogConfig extends DataDogCustomConfig, DatadogDefConfig {}
